import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Upload, X, File, FileText } from "lucide-react";
import type { UploadableDocument } from "@/lib/vehicleStorage";
import { getPublicErrorMessage } from "@/lib/errors";

interface DocumentUploadProps {
  documents: UploadableDocument[];
  onDocumentsUpdate: (documents: UploadableDocument[]) => void;
  maxDocuments?: number;
  disabled?: boolean;
}

const DOCUMENT_TYPES = [
  { value: "crlv", label: "CRLV (Documento do Veículo)" },
  { value: "laudo", label: "Laudo de Vistoria" },
  { value: "ipva", label: "Comprovante IPVA" },
  { value: "seguro", label: "Apólice de Seguro" },
  { value: "outros", label: "Outros Documentos" },
];

export function DocumentUpload({
  documents,
  onDocumentsUpdate,
  maxDocuments = 10,
  disabled = false,
}: DocumentUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [selectedType, setSelectedType] = useState<string>("");
  const { toast } = useToast();

  const getErrorMessage = (error: unknown) => {
    if (error instanceof Error) {
      return getPublicErrorMessage(error.message, "Erro ao enviar documento.")
    }

    return "Erro ao enviar documento."
  }

  const triggerFilePicker = () => {
    if (!selectedType) {
      toast({
        title: "Selecione o tipo do documento",
        description: "Escolha o tipo antes de selecionar o arquivo.",
        variant: "destructive",
      });
      return;
    }

    document.getElementById("document-upload")?.click();
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedType) {
      if (!selectedType) {
        toast({
          title: "Tipo de documento obrigatório",
          description: "Selecione o tipo de documento antes de fazer o upload.",
          variant: "destructive",
        });
      }
      return;
    }

    if (documents.length >= maxDocuments) {
      toast({
        title: "Limite excedido",
        description: `Máximo de ${maxDocuments} documentos permitidos.`,
        variant: "destructive",
      });
      return;
    }

    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/jpg",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Tipo de arquivo não suportado",
        description: "Apenas PDF, imagens (JPG, PNG) e documentos Word são permitidos.",
        variant: "destructive",
      });
      return;
    }

    setUploading(true);
    try {
      const newDocument: UploadableDocument = {
        tipo_documento: selectedType,
        url: URL.createObjectURL(file),
        nome_arquivo: file.name,
        file,
      };

      onDocumentsUpdate([...documents, newDocument]);
      setSelectedType("");

      toast({
        title: "Documento enviado",
        description: "Documento adicionado com sucesso!",
      });
    } catch (error: unknown) {
      console.error("Erro no upload:", error);
      toast({
        title: "Erro no upload",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  const removeDocument = (index: number) => {
    const newDocuments = documents.filter((_, i) => i !== index);
    onDocumentsUpdate(newDocuments);
    toast({
      title: "Documento removido",
      description: "Documento removido da lista.",
    });
  };

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split(".").pop()?.toLowerCase();
    if (extension === "pdf") return <FileText className="h-8 w-8 text-red-500" />;
    if (["jpg", "jpeg", "png"].includes(extension || "")) return <File className="h-8 w-8 text-blue-500" />;
    return <File className="h-8 w-8 text-gray-500" />;
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="document-type">Tipo de Documento *</Label>
          <Select value={selectedType} onValueChange={setSelectedType} disabled={disabled}>
            <SelectTrigger>
              <SelectValue placeholder="Selecione o tipo" />
            </SelectTrigger>
            <SelectContent>
              {DOCUMENT_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="document-upload">Arquivo do Documento</Label>
          <div className="flex items-center gap-2">
            <Input
              id="document-upload"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              onChange={handleFileUpload}
              disabled={disabled || uploading}
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              onClick={triggerFilePicker}
              disabled={disabled || uploading}
              className="w-full"
            >
              <Upload className="h-4 w-4 mr-2" />
              {uploading ? "Enviando..." : "Selecionar Arquivo"}
            </Button>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Selecione o tipo do documento e depois escolha o arquivo.
          </p>
        </div>
      </div>

      {documents.length > 0 && (
        <div>
          <Label>Documentos Enviados ({documents.length}/{maxDocuments})</Label>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-2">
            {documents.map((document, index) => (
              <Card key={index} className="relative group">
                <CardContent className="p-3">
                  <div className="flex items-start gap-3">
                    {getFileIcon(document.nome_arquivo)}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {DOCUMENT_TYPES.find((t) => t.value === document.tipo_documento)?.label}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">{document.nome_arquivo}</p>
                    </div>
                  </div>
                  {!disabled && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeDocument(index)}
                      className="absolute top-1 right-1 h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {documents.length === 0 && (
        <div className="text-center py-8 border-2 border-dashed border-muted-foreground/25 rounded-lg">
          <Upload className="h-12 w-12 mx-auto text-muted-foreground/50 mb-2" />
          <p className="text-sm text-muted-foreground">Nenhum documento enviado ainda</p>
          <p className="text-xs text-muted-foreground">Selecione o tipo e faça upload dos documentos do veículo</p>
        </div>
      )}
    </div>
  );
}
